const TIMEZONE_STORAGE_KEY = "user-timezone";

/**
 * Get user's timezone from localStorage or detect from browser.
 * The timezone is cached in localStorage so it persists across page loads.
 * User can log out and back in to update if they change timezones.
 */
export function getUserTimezone(): string {
	if (typeof window === "undefined") return "UTC";

	const stored = localStorage.getItem(TIMEZONE_STORAGE_KEY);
	if (stored) return stored;

	// Detect and store the browser's timezone
	const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
	localStorage.setItem(TIMEZONE_STORAGE_KEY, detected);
	return detected;
}

/**
 * Helper function to format timestamp in user's local timezone
 * Format: "MM/DD/YY h:mm am/pm" or "MM/DD/YY" if dateOnly
 */
export function formatTimestamp(
	isoString: string | null | undefined,
	options?: { dateOnly?: boolean },
): string {
	if (!isoString) return "";

	const date = new Date(isoString);
	const timezone = getUserTimezone();

	if (options?.dateOnly) {
		return date.toLocaleDateString("en-US", {
			timeZone: timezone,
			month: "2-digit",
			day: "2-digit",
			year: "2-digit",
		});
	}

	return date
		.toLocaleString("en-US", {
			timeZone: timezone,
			month: "2-digit",
			day: "2-digit",
			year: "2-digit",
			hour: "numeric",
			minute: "2-digit",
			hour12: true,
		})
		.toLowerCase();
}

/**
 * Helper function to format timestamp as "MMM DD, YYYY" in user's local timezone.
 * Optionally includes time: "MMM DD, YYYY h:mmAM/PM".
 *
 * For date-only values (e.g. due dates stored as midnight UTC), pass dateOnly: true
 * so the calendar date is shown without timezone shift (e.g. 2026-02-01T00:00:00Z → "Feb 1, 2026").
 */
export function formatLongDate(
	isoString: string | null | undefined,
	options?: { includeTime?: boolean; dateOnly?: boolean },
): string {
	if (!isoString) return "";

	const date = new Date(isoString);
	const timezone = getUserTimezone();

	// Date-only (e.g. due date): show the calendar date from the ISO string, no timezone shift
	const zone = options?.dateOnly ? "UTC" : timezone;
	const dateStr = date.toLocaleDateString("en-US", {
		timeZone: zone,
		month: "short",
		day: "numeric",
		year: "numeric",
	});

	if (options?.includeTime) {
		const timeZoneForTime = options?.dateOnly ? "UTC" : timezone;
		const timeStr = date
			.toLocaleTimeString("en-US", {
				timeZone: timeZoneForTime,
				hour: "numeric",
				minute: "2-digit",
				hour12: true,
			})
			.replace(" ", "");
		return `${dateStr} ${timeStr}`;
	}

	return dateStr;
}
